import { UserCredentials, AuthResponse, UserType } from '../types/auth';

export class AuthService {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = process.env.REACT_APP_API_URL || 'https://api.usesieve.com') {
    // Ensure we always have a valid URL
    if (!baseUrl) {
      this.baseUrl = 'https://api.usesieve.com';
    } else {
      this.baseUrl = baseUrl;
    }
    console.log('API URL:', process.env.REACT_APP_API_URL);
    console.log('Base URL:', this.baseUrl);
  }

  setToken(token: string): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit): Promise<T> {
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      ...options.headers,
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();
      console.log('Raw response data:', data);

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `HTTP error! status: ${response.status}`;
        console.error('Server error:', errorMessage, 'Full response:', data);
        throw new Error(errorMessage);
      }

      // Validate AuthResponse structure for login endpoint
      if (endpoint === '/api/v1/auth/login') {
        if (!data.token || !data.user_type || !data.user_id) {
          console.error('Invalid login response structure:', data);
          throw new Error('Server returned an invalid response format');
        }
      }

      return data;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  async login(credentials: UserCredentials): Promise<AuthResponse> {
    try {
      console.log('Sending login request to:', `${this.baseUrl}/api/v1/auth/login`);
      console.log('With credentials:', { email: credentials.email });

      const response = await this.request<AuthResponse>('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
        credentials: 'include',
      });

      console.log('Login response received:', response);
      this.setToken(response.token);
      localStorage.setItem('userType', response.user_type);
      return response;
    } catch (error) {
      console.error('Login request failed:', error);
      throw error;
    }
  }

  async register(userData: UserCredentials & { full_name: string; user_type: UserType; invite_code?: string }): Promise<AuthResponse> {
    console.log('Sending registration request with data:', { 
      email: userData.email, 
      user_type: userData.user_type,
      has_invite_code: !!userData.invite_code 
    });
    
    return this.request<AuthResponse>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    console.log('Sending password reset request for email:', email);
    
    return this.request<{ message: string }>('/api/v1/auth/password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<{ message: string }> {
    console.log('Confirming password reset');
    
    return this.request<{ message: string }>('/api/v1/auth/password-reset-confirm', {
      method: 'POST',
      body: JSON.stringify({ oobCode, newPassword }),
    });
  }

  async validateToken(token: string): Promise<boolean> {
    try {
      const response = await this.request<{ valid: boolean }>('/api/v1/auth/validate', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.valid;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }

  async generateInviteCode(data: { role: 'admin' | 'worker'; expiryDays?: number; codeString?: string }): Promise<{ inviteCode: string }> {
    try {
      // Transform the data to match the backend's expected structure
      const backendData = {
        role: data.role,
        expiry_days: data.expiryDays,
        code: data.codeString
      };

      const response = await this.request<{ invite_code: string }>('/api/v1/admin/generate_invite', {
        method: 'POST',
        body: JSON.stringify(backendData),
      });
      return {
        inviteCode: response.invite_code
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 
                         typeof error === 'string' ? error :
                         'Failed to generate invite code';
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Refresh the authentication token using a refresh token
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      const response = await this.request<AuthResponse>('/api/v1/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      // Update stored tokens
      if (response.token) {
        localStorage.setItem('authToken', response.token);
        this.setToken(response.token);
      }
      
      if (response.refresh_token) {
        localStorage.setItem('refreshToken', response.refresh_token);
      }
      
      return response;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 
                         typeof error === 'string' ? error :
                         'Failed to refresh token';
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Update the user's last activity timestamp
   */
  async updateActivity(): Promise<{ status: string }> {
    try {
      return this.request<{ status: string }>('/api/v1/auth/activity', {
        method: 'POST',
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 
                         typeof error === 'string' ? error :
                         'Failed to update activity';
      console.error(errorMessage);
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Logout the current user
   */
  logout(): void {
    // Clear token from memory
    this.token = null;
    
    // Clear tokens from localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
  }
}

export const authService = new AuthService();
